import React, { useMemo, useState } from 'react';
import { Plus, Trash, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import recordOperationsDownload from '../../utils/recordOperationsDownload';
import { SearchBar } from '../SearchBar/SearchBar';
import { Modal } from '../Modal/Modal';
// Eliminamos el formulario completo para edición; usaremos formulario mínimo en este módulo
// Preferencias de columnas removidas; render fijo de columnas

export function MaterialsModule({ materials, onAdd, onUpdate, onDelete }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newData, setNewData] = useState({ code: '', name: '', unit: 'Unidad', type: 'Material' });
  const [editData, setEditData] = useState({ code: '', name: '', unit: 'Unidad', observations: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const [typeFilter, setTypeFilter] = useState('Todos');
  // Columnas fijas (sin preferencias por usuario)
  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const typeOptions = useMemo(() => {
    const set = new Set();
    (materials || []).forEach((m) => {
      const t = String(m.category || m.type || 'Material').trim();
      if (t) set.add(t);
    });
    const sorted = Array.from(set).sort((a, b) => a.localeCompare(b));
    return ['Todos', ...sorted];
  }, [materials]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byQuery = (!q)
      ? materials
      : materials.filter((m) =>
          [m.code, m.name, m.brand, m.category, m.location, m.serial]
            .some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    if (typeFilter === 'Todos') return byQuery;
    const f = norm(typeFilter);
    return byQuery.filter((m) => norm(m.category || m.type || 'Material').includes(f));
  }, [query, materials, typeFilter]);

  const openNew = () => {
    setEditing(null);
    setErrorMsg('');
    setIsOpen(true);
  };

  // Edición deshabilitada en UI actual; se elimina función no usada.

  const closeModal = () => {
    setIsOpen(false);
    setEditing(null);
    setErrorMsg('');
  };

  const handleSave = (data) => {
    const isEditExisting = editing && typeof editing.id !== 'undefined';
    const code = (data.code || '').trim();
    const name = (data.name || '').trim();
    const unit = (data.unit || '').trim();
    const type = (data.type || 'Material').trim();

    if (!code) { setErrorMsg('El código es obligatorio.'); return; }
    if (!name) { setErrorMsg('El nombre es obligatorio.'); return; }

    const normalized = code.toLowerCase();
    const exists = materials.some(m => ((m.code || '').trim().toLowerCase() === normalized) && (!isEditExisting || m.id !== editing.id));
    if (exists) { setErrorMsg('El código ya existe. Ingrese uno diferente.'); return; }

    if (isEditExisting) {
      onUpdate(editing.id, { code, name, unit, observations: data.observations || '' });
    } else {
      // Guardar nuevo material con campos mínimos y valores por defecto
      // Enviar explícitamente category a backend igual al tipo seleccionado
      onAdd({ code, name, unit, type, category: type, status: 'Activo', stockActual: 0, stockMin: 0, stockMax: 0 });
    }
    closeModal();
  };


  // Se elimina el indicador de existencias generales en Materiales.

  const statusBadge = (status) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';
    const cls = status === 'Activo' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200';
    return <span className={`${base} ${cls}`}>{status}</span>;
  };

  const exportToXlsx = () => {
    try {
      const rows = (materials || []).map((m) => ({
        'Código': m.code || '',
        'Nombre': m.name || '',
        'Tipo': m.category || m.type || 'Material',
        'Estado': m.status || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Materiales');
      const filename = `materiales_${new Date().toISOString().slice(0,10)}.xlsx`;
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Catálogo de materiales' });
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Error exportando materiales a .xlsx', err);
      alert('No se pudo exportar el archivo .xlsx.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title">Gestión de Materiales</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportToXlsx}
            className="inline-flex items-center gap-2 text-white px-3 py-2 rounded-md text-sm"
            style={{ backgroundColor: '#04873B' }}
          >
            <Download size={18} /> Exportar .xlsx
          </button>
          <button
            type="button"
            onClick={openNew}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm"
          >
            <Plus size={18} /> Nuevo Material
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex-1 min-w-[240px]">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Buscar por nombre o código..."
          />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="text-sm text-gray-600">Tipo:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border rounded px-2 py-2 text-sm"
          >
            {typeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {/** Botón de exportación movido al encabezado para garantizar visibilidad **/}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 text-left">Código</th>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-left">Tipo</th>
              {/* Columna de existencias generales eliminada */}
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-4 py-2">{m.code}</td>
                <td className="px-4 py-2">{m.name}</td>
                <td className="px-4 py-2">{m.category || m.type || 'Material'}</td>
                {/* Columna de existencias generales eliminada */}
                <td className="px-4 py-2">{statusBadge(m.status)}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => onDelete(m.id)}
                      aria-label="Eliminar"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">No se encontraron materiales.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} title={editing ? 'Editar Material' : 'Nuevo Material'}>
        {editing ? (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(editData); }}
            className="grid grid-cols-2 gap-4"
          >
            {errorMsg && (
              <div className="col-span-2 text-red-600 text-sm mb-2">{errorMsg}</div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Código *</label>
              <input
                type="text"
                value={editData.code}
                onChange={(e) => setEditData({ ...editData, code: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Unidad *</label>
              <select
                value={editData.unit}
                onChange={(e) => setEditData({ ...editData, unit: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Unidad</option>
                <option>Caja</option>
                <option>Paquete</option>
                <option>Litro</option>
                <option>Kilogramo</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Observación</label>
              <textarea
                value={editData.observations}
                onChange={(e) => setEditData({ ...editData, observations: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="3"
                placeholder="Notas u observaciones del material"
              ></textarea>
            </div>
            <div className="col-span-2 flex gap-2 mt-2">
              <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">Guardar</button>
              <button type="button" onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded">Cancelar</button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(newData); }}
            className="grid grid-cols-2 gap-4"
          >
            {errorMsg && (
              <div className="col-span-2 text-red-600 text-sm mb-2">{errorMsg}</div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Código *</label>
              <input
                type="text"
                value={newData.code}
                onChange={(e) => setNewData({ ...newData, code: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={newData.name}
                onChange={(e) => setNewData({ ...newData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Unidad *</label>
              <select
                value={newData.unit}
                onChange={(e) => setNewData({ ...newData, unit: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Unidad</option>
                <option>Caja</option>
                <option>Paquete</option>
                <option>Litro</option>
                <option>Kilogramo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo *</label>
              <select
                value={newData.type}
                onChange={(e) => setNewData({ ...newData, type: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Material</option>
                <option>Herramientas</option>
                <option>Tecnologia</option>
                <option>Dotacion</option>
                <option>Servicios-Generales</option>
              </select>
            </div>
            <div className="col-span-2 flex gap-2 mt-2">
              <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">Guardar</button>
              <button type="button" onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded">Cancelar</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
